// src/services/announcementAnalytics.js
// Comprehensive analytics tracking for announcements

import { getFirestore, collection, addDoc, doc, updateDoc, increment, serverTimestamp, query, where, getDocs } from 'firebase/firestore';
import { resolvePricingKeyFromUserData } from './pricingTierService';

const db = getFirestore();

/**
 * Track announcement view
 */
export async function trackAnnouncementView(announcementId, userId, userRole, userPricing, variant = 'A') {
    try {
        await addDoc(collection(db, 'announcement_analytics'), {
            announcementId,
            userId,
            userRole,
            userPricing,
            variant, // For A/B testing
            eventType: 'view',
            timestamp: serverTimestamp(),
            sessionId: getSessionId(),
            userAgent: navigator.userAgent,
            viewport: {
                width: window.innerWidth,
                height: window.innerHeight
            }
        });

        // Update announcement aggregate stats
        const announcementRef = doc(db, 'announcements', announcementId);
        await updateDoc(announcementRef, {
            [`stats.views_${variant}`]: increment(1),
            'stats.totalViews': increment(1),
            'stats.lastViewed': serverTimestamp()
        });
    } catch (error) {
        console.error('Error tracking view:', error);
    }
}

/**
 * Track announcement click (CTA button)
 */
export async function trackAnnouncementClick(announcementId, userId, userRole, ctaLink, variant = 'A') {
    try {
        await addDoc(collection(db, 'announcement_analytics'), {
            announcementId,
            userId,
            userRole,
            variant,
            eventType: 'click',
            ctaLink,
            timestamp: serverTimestamp(),
            sessionId: getSessionId()
        });

        // Update announcement aggregate stats
        const announcementRef = doc(db, 'announcements', announcementId);
        await updateDoc(announcementRef, {
            [`stats.clicks_${variant}`]: increment(1),
            'stats.totalClicks': increment(1),
            'stats.lastClicked': serverTimestamp()
        });
    } catch (error) {
        console.error('Error tracking click:', error);
    }
}

/**
 * Track announcement dismissal
 */
export async function trackAnnouncementDismissal(announcementId, userId, timeOnScreen, variant = 'A') {
    try {
        await addDoc(collection(db, 'announcement_analytics'), {
            announcementId,
            userId,
            variant,
            eventType: 'dismiss',
            timeOnScreen, // milliseconds
            timestamp: serverTimestamp(),
            sessionId: getSessionId()
        });

        // Update announcement aggregate stats
        const announcementRef = doc(db, 'announcements', announcementId);
        await updateDoc(announcementRef, {
            [`stats.dismissals_${variant}`]: increment(1),
            'stats.totalDismissals': increment(1),
            'stats.avgTimeOnScreen': timeOnScreen // This will need server-side averaging
        });
    } catch (error) {
        console.error('Error tracking dismissal:', error);
    }
}

/**
 * Get comprehensive analytics for an announcement
 */
export async function getAnnouncementAnalytics(announcementId, dateRange = null) {
    try {
        let analyticsQuery = query(
            collection(db, 'announcement_analytics'),
            where('announcementId', '==', announcementId)
        );

        if (dateRange) {
            // Filter by date range if provided
            analyticsQuery = query(
                analyticsQuery,
                where('timestamp', '>=', dateRange.start),
                where('timestamp', '<=', dateRange.end)
            );
        }

        const snapshot = await getDocs(analyticsQuery);
        const events = [];
        snapshot.forEach(doc => {
            events.push({ id: doc.id, ...doc.data() });
        });

        // Calculate metrics
        const views = events.filter(e => e.eventType === 'view');
        const clicks = events.filter(e => e.eventType === 'click');
        const dismissals = events.filter(e => e.eventType === 'dismiss');

        // A/B testing breakdown
        const viewsByVariant = {
            A: views.filter(v => v.variant === 'A').length,
            B: views.filter(v => v.variant === 'B').length
        };
        const clicksByVariant = {
            A: clicks.filter(c => c.variant === 'A').length,
            B: clicks.filter(c => c.variant === 'B').length
        };

        // Calculate CTR
        const ctrA = viewsByVariant.A > 0 ? (clicksByVariant.A / viewsByVariant.A) * 100 : 0;
        const ctrB = viewsByVariant.B > 0 ? (clicksByVariant.B / viewsByVariant.B) * 100 : 0;

        // User segmentation
        const viewsByRole = {};
        const viewsByPricing = {};
        views.forEach(v => {
            viewsByRole[v.userRole] = (viewsByRole[v.userRole] || 0) + 1;
            viewsByPricing[v.userPricing] = (viewsByPricing[v.userPricing] || 0) + 1;
        });

        // Time on screen average
        const avgTimeOnScreen = dismissals.length > 0
            ? dismissals.reduce((sum, d) => sum + (d.timeOnScreen || 0), 0) / dismissals.length
            : 0;

        return {
            totalViews: views.length,
            totalClicks: clicks.length,
            totalDismissals: dismissals.length,
            viewsByVariant,
            clicksByVariant,
            ctr: {
                A: ctrA,
                B: ctrB,
                overall: views.length > 0 ? (clicks.length / views.length) * 100 : 0
            },
            avgTimeOnScreen: avgTimeOnScreen / 1000, // Convert to seconds
            viewsByRole,
            viewsByPricing,
            uniqueUsers: new Set(views.map(v => v.userId)).size,
            events
        };
    } catch (error) {
        console.error('Error fetching analytics:', error);
        return null;
    }
}

/**
 * Get session ID (or create one)
 */
function getSessionId() {
    let sessionId = sessionStorage.getItem('announcement_session');
    if (!sessionId) {
        sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        sessionStorage.setItem('announcement_session', sessionId);
    }
    return sessionId;
}

/**
 * Determine which variant to show for A/B testing
 */
export function getABTestVariant(announcementId, userId) {
    // Consistent assignment based on user ID
    // This ensures the same user always sees the same variant
    const hash = hashCode(userId + announcementId);
    return hash % 2 === 0 ? 'A' : 'B';
}

function hashCode(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash);
}

/**
 * Check if user matches targeting criteria
 */
export function matchesTargeting(announcement, userData) {
    if (!announcement.targeting || !announcement.targeting.enabled) {
        return true; // No targeting = show to everyone
    }

    const targeting = announcement.targeting;

    // Role targeting
    if (targeting.roles && targeting.roles.length > 0) {
        if (!targeting.roles.includes(userData.role)) {
            return false;
        }
    }

    // Pricing tier targeting (legacy Pricing "default" resolves as Pricing A)
    if (targeting.pricingTiers && targeting.pricingTiers.length > 0) {
        const userTier = resolvePricingKeyFromUserData(userData);
        const allowed = targeting.pricingTiers.map((t) =>
            resolvePricingKeyFromUserData({ Pricing: t })
        );
        if (!allowed.includes(userTier)) {
            return false;
        }
    }

    // Custom field targeting
    if (targeting.customFields) {
        for (const [field, value] of Object.entries(targeting.customFields)) {
            if (userData[field] !== value) {
                return false;
            }
        }
    }

    // New vs returning users
    if (targeting.userType) {
        const accountAge = userData.createdAt ? Date.now() - userData.createdAt.toDate().getTime() : 0;
        const isNewUser = accountAge < 7 * 24 * 60 * 60 * 1000; // Less than 7 days

        if (targeting.userType === 'new' && !isNewUser) return false;
        if (targeting.userType === 'returning' && isNewUser) return false;
    }

    return true;
}

export default {
    trackAnnouncementView,
    trackAnnouncementClick,
    trackAnnouncementDismissal,
    getAnnouncementAnalytics,
    getABTestVariant,
    matchesTargeting
};

